import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';

import { FilterOutlined } from '@ant-design/icons';
import { Modal, Input, Space, Typography, Row, Col, message } from 'antd';

import { RuleBlockCard } from './RuleBlockCard';
import { adsRulesApi } from '../../../../shared/common/api/endpoints/adsRulesApi';
import { MOCK_ACCOUNTS, MOCK_CAMPAIGNS, MOCK_ADGROUPS, MOCK_ADS } from '../../constants/data';
import { FormState, RuleBlock, RuleTableItem, DictionariesResponse, HierarchicalActionTypes } from '../../types';
import { uid } from '../../utils/helpers';
import { ScopeToggle } from '../common/ScopeToggle';
import { AccountsMultiSelect } from '../selectors/AccountsMultiSelect';
import { AdGroupsMultiSelect } from '../selectors/AdGroupsMultiSelect';
import { AdsMultiSelect } from '../selectors/AdsMultiSelect';
import { AdSourceSelect } from '../selectors/AdSourceSelect';
import { CampaignsMultiSelect } from '../selectors/CampaignsMultiSelect';

const { Text } = Typography;

interface RuleFormModalProps {
  open: boolean;
  onCancel: () => void;
  onSave: (rule: RuleBlock, formData: Partial<FormState>) => void;
  editingRule?: RuleTableItem;
  initialFormData?: Partial<FormState>;
  actionTypes?: HierarchicalActionTypes;
}

export function RuleFormModal({ open, onCancel, onSave, editingRule, initialFormData, actionTypes }: RuleFormModalProps) {
  const [showFilters, setShowFilters] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [dictionaries, setDictionaries] = useState<DictionariesResponse['data'] | null>(null);
  const [dictionariesLoading, setDictionariesLoading] = useState(false);

  const updateCurrentRule = useCallback((updater: (prev: RuleBlock) => RuleBlock) => {
    setCurrentRule(updater);
    setHasUnsavedChanges(true);
  }, []);

  const updateFormData = useCallback((updater: (prev: Partial<FormState>) => Partial<FormState>) => {
    setFormData(updater);
    setHasUnsavedChanges(true);
  }, []);

  // Обертка для RuleBlockCard onChange - мemoized to prevent infinite loops
  const handleRuleChange = useCallback((newRule: RuleBlock) => {
    setCurrentRule(newRule);
    setHasUnsavedChanges(true);
  }, []);

  const [formData, setFormData] = useState<Partial<FormState>>({
    adSource: 'yandex_direct',
    accounts: [],
    campaignIds: [],
    adGroupIds: [],
    adIds: [],
    scope: ['campaigns'],
    lookbackDays: 5,
    checkEveryDays: 5,
  });

  const [currentRule, setCurrentRule] = useState<RuleBlock>({
    id: uid(),
    name: 'Новое правило',
    periodDays: 5,
    groups: [
      {
        id: uid(),
        logic: 'OR',
        periodDays: 5,
        atoms: [
          { id: uid(), metric: 'users_count', comparator: '>', value: 1000, periodDays: 5 },
        ],
      },
    ],
    actions: {
      budgetAction: 'none',
      budgetAmount: 1,
      budgetUnit: 'rub',
      sendTelegram: false,
      tgMessage: '',
      tgRecipients: []
    },
  });

  // Function to fetch dictionaries from API
  const fetchDictionaries = async () => {
    setDictionariesLoading(true);

    try {
      const response = await adsRulesApi.fetchDictionaries({
        dictionaries: 'action_types,action_units,object_types,ad_accounts,sources,telegram_users'
      });

      if (response.data.success) {
        setDictionaries(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching dictionaries:', error);
    } finally {
      setDictionariesLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchDictionaries();
      setHasUnsavedChanges(false); // Сбрасываем флаг при открытии

      if (editingRule) {
        setCurrentRule({ ...editingRule });
      } else {
        setCurrentRule({
          id: uid(),
          name: 'Новое правило',
          periodDays: 5,
          groups: [
            {
              id: uid(),
              logic: 'OR',
              periodDays: 5,
              atoms: [
                { id: uid(), metric: 'users_count', comparator: '>', value: 1000, periodDays: 5 },
              ],
            },
          ],
          actions: {
            budgetAction: 'none',
            budgetAmount: 1,
            budgetUnit: 'rub',
            sendTelegram: false,
            tgMessage: '',
            tgRecipients: []
          },
        });
      }

      if (initialFormData) {
        setFormData({ ...formData, ...initialFormData });
      }

      // Если пришли сохраненные фильтры в редактировании — показываем блок фильтров
      const fd = editingRule?.formData || initialFormData;
      if (fd) {
        const hasFilters = (fd.campaignIds && fd.campaignIds.length > 0) ||
          (fd.adGroupIds && fd.adGroupIds.length > 0) ||
          (fd.adIds && fd.adIds.length > 0);
        if (hasFilters) setShowFilters(true);
      }
    }
  }, [open, editingRule, initialFormData]);

  // Set formData after dictionaries are loaded (for editing mode)
  useEffect(() => {
    if (open && editingRule && editingRule.formData && dictionaries) {
      setFormData(editingRule.formData);
      const fd = editingRule.formData;
      const hasFilters = (fd.campaignIds && fd.campaignIds.length > 0) ||
        (fd.adGroupIds && fd.adGroupIds.length > 0) ||
        (fd.adIds && fd.adIds.length > 0);
      if (hasFilters) setShowFilters(true);
    }
  }, [open, editingRule, dictionaries]);

  // Helper functions to get data from API with fallback to mock data
  const getAccountsData = (): any[] => {
    if (dictionaries?.ad_accounts && formData.adSource) {
      const sourceId = formData.adSource === 'yandex_direct' ? '2' : '9';
      const apiAccounts = dictionaries.ad_accounts[sourceId] || [];
      // Преобразуем API данные в формат, ожидаемый селекторами
      return apiAccounts.map((account: any) => ({
        id: account.id.toString(), // Преобразуем в строку для селектора
        name: account.name
      }));
    }
    return MOCK_ACCOUNTS;
  };

  const getCampaignsData = (): any[] => {
    // При редактировании используем сохранённые варианты с именами,
    // чтобы метки в селекте отображали name, а не ID
    if (editingRule?.formData && (editingRule.formData as any).campaignOptions) {
      return (editingRule.formData as any).campaignOptions as Array<{ id: string; name: string }>;
    }
    // Иначе — мок до загрузки через поиск
    return MOCK_CAMPAIGNS;
  };

  const getAdGroupsData = (): any[] => {
    // For now, ad groups are not in the API response, so use mock data
    return MOCK_ADGROUPS;
  };

  const getAdsData = (): any[] => {
    // For now, ads are not in the API response, so use mock data
    return MOCK_ADS;
  };

  const getActionTypesData = (): HierarchicalActionTypes | null => {
    // Используем переданные actionTypes или загружаем из API
    if (actionTypes) {
      return actionTypes;
    }
    if (dictionaries?.action_types) {
      return dictionaries.action_types;
    }
    return null;
  };

  const getTelegramUsers = (): { id: string; name: string; username: string }[] => {
    if (dictionaries?.telegram_users) {
      return dictionaries.telegram_users.map((user: any) => ({
        id: user.id.toString(),
        name: user.name,
        username: ''
      }));
    }
    return [];
  };

  const scopeOne = formData.scope?.[0] || 'campaigns';
  const isEditMode = Boolean(editingRule && !(editingRule as any).isDuplicate);
  const setScopeOne = (k: 'campaigns' | 'adGroups' | 'ads') => {
    updateFormData(prev => ({
      ...prev,
      scope: [k],
      campaignIds: [],
      adGroupIds: [],
      adIds: []
    }));
    // Сбрасываем действия при изменении типа проверяемых объектов
    updateCurrentRule(prev => ({
      ...prev,
      actions: {
        ...prev.actions,
        budgetAction: 'none',
        actionType: 'none',
        budgetAmount: 1
      }
    }));
  };

  const getFilterButtonText = () => {
    // Проверяем, есть ли выбранные фильтры
    const hasFilters = (formData.campaignIds && formData.campaignIds.length > 0) ||
      (formData.adGroupIds && formData.adGroupIds.length > 0) ||
      (formData.adIds && formData.adIds.length > 0);

    if (hasFilters) {
      return 'Сбросить фильтр';
    }

    if (scopeOne === 'campaigns') return 'Отфильтровать кампании';
    if (scopeOne === 'adGroups') return 'Отфильтровать группы объявлений';
    return 'Отфильтровать объявления';
  };

  const handleFilterClick = () => {
    // Проверяем, есть ли выбранные фильтры
    const hasFilters = (formData.campaignIds && formData.campaignIds.length > 0) ||
      (formData.adGroupIds && formData.adGroupIds.length > 0) ||
      (formData.adIds && formData.adIds.length > 0);

    if (hasFilters) {
      // Сбрасываем все фильтры
      updateFormData(prev => ({
        ...prev,
        campaignIds: [],
        adGroupIds: [],
        adIds: []
      }));
      // Скрываем блок фильтров
      setShowFilters(false);
    } else {
      // Переключаем видимость фильтров
      setShowFilters(!showFilters);
    }
  };

  const handleSave = () => {
    // Валидация согласно API требованиям
    const validationErrors: string[] = [];

    // Проверяем обязательные поля
    if (!currentRule.name.trim() || currentRule.name.length > 1000) {
      validationErrors.push('Название правила обязательно и не должно превышать 1000 символов');
    }

    if (!formData.accounts || formData.accounts.length === 0) {
      validationErrors.push('Необходимо выбрать хотя бы один рекламный кабинет');
    }

    if (!currentRule.groups || currentRule.groups.length === 0 ||
      !currentRule.groups.some(group => group.atoms && group.atoms.length > 0)) {
      validationErrors.push('Необходимо добавить хотя бы одно условие');
    }

    if (!currentRule.periodDays || currentRule.periodDays < 1) {
      validationErrors.push('Частота проверки должна быть больше 0 дней');
    }

    for (const group of currentRule.groups || []) {

      for (const atom of group.atoms || []) {
          const periodDays = atom.periodDays ?? currentRule.periodDays;
          if (atom.value === '' || atom.value === null || atom.value === undefined) {
            validationErrors.push(
              `Введите значение за последние ${periodDays} дней`
            );
            continue;
          }

          const n = Number(atom.value);

          if (Number.isNaN(n)) {
            validationErrors.push(
              `Значение должно быть числом за последние ${periodDays} дней`
            );
            continue;
          } 
        }
      
    }

   
    if ((currentRule.actions.budgetAction === 'increase' || currentRule.actions.budgetAction === 'decrease')) {
      if (!currentRule.actions.budgetAmount || currentRule.actions.budgetAmount <= 0) {
        validationErrors.push('Для изменения бюджета необходимо указать положительную сумму');
      }
      if (!currentRule.actions.budgetUnit) {
        validationErrors.push('Для изменения бюджета необходимо указать единицу измерения');
      }
    }

  
    if (currentRule.actions.sendTelegram) {
      if (!currentRule.actions.tgMessage || currentRule.actions.tgMessage.trim() === '') {
        validationErrors.push('Для отправки Telegram необходимо указать текст сообщения');
      }
      if (!currentRule.actions.tgRecipients || currentRule.actions.tgRecipients.length === 0) {
        validationErrors.push('Для отправки Telegram необходимо выбрать получателей');
      }
    }

    // Проверяем, что есть хотя бы одно действие
    const hasBudgetAction = currentRule.actions.budgetAction !== 'none';
    const hasTelegramAction = currentRule.actions.sendTelegram;
    const hasDisableAction = currentRule.actions.budgetAction === 'disable';

    if (!hasBudgetAction && !hasTelegramAction && !hasDisableAction) {
      validationErrors.push('Необходимо выбрать хотя бы одно действие');
    }

    if (validationErrors.length > 0) {
      // Показываем первую ошибку валидации
      message.error(validationErrors[0]);
      return;
    }

    onSave(currentRule, formData);
    setHasUnsavedChanges(false);
  };

  // Функция для обработки закрытия модального окна с предупреждением
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: 'Несохраненные изменения',
        content: 'У вас есть несохраненные изменения. Вы уверены, что хотите закрыть модальное окно?',
        okText: 'Да, закрыть',
        cancelText: 'Отмена',
        onOk: () => {
          setHasUnsavedChanges(false);
          onCancel();
        },
      });
    } else {
      onCancel();
    }
  };

  // Проверяем, можно ли сохранить правило согласно API требованиям
  const hasValidName = currentRule.name.trim().length > 0 && currentRule.name.length <= 1000;
  const hasValidAccount = formData.accounts && formData.accounts.length > 0;
  const hasValidConditions = currentRule.groups && currentRule.groups.length > 0 &&
    currentRule.groups.some(group => group.atoms && group.atoms.length > 0);
  const hasValidFrequency = currentRule.periodDays && currentRule.periodDays >= 1;

  // Проверяем действия
  const hasBudgetAction = currentRule.actions.budgetAction !== 'none';
  const hasTelegramAction = currentRule.actions.sendTelegram;
  const hasDisableAction = currentRule.actions.budgetAction === 'disable';
  const hasMonitoringAction = currentRule.actions.budgetAction === 'none'; // "Ничего не меняем" - это тоже валидное действие
  const hasAnyAction = hasBudgetAction || hasTelegramAction || hasDisableAction || hasMonitoringAction;

  // Проверяем корректность бюджетных действий
  const budgetActionValid = currentRule.actions.budgetAction === 'none' ||
    currentRule.actions.budgetAction === 'disable' ||
    (currentRule.actions.budgetAmount && currentRule.actions.budgetAmount > 0 && currentRule.actions.budgetUnit);

  // Проверяем корректность Telegram действий
  const telegramActionValid = !currentRule.actions.sendTelegram ||
    (currentRule.actions.tgMessage && currentRule.actions.tgMessage.trim() !== '' &&
      currentRule.actions.tgRecipients && currentRule.actions.tgRecipients.length > 0);

  const canSave = hasValidName && hasValidAccount && hasValidConditions && hasValidFrequency &&
    hasAnyAction && budgetActionValid && telegramActionValid;

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      onOk={handleSave}
      okText={editingRule ? 'Сохранить' : 'Создать'}
      okButtonProps={{ disabled: !canSave }}
      cancelText="Отмена"
      maskClosable={false}
      width="90%"
      style={{
        top: 20,
        maxWidth: 870,
        minWidth: 870
      }}
      bodyStyle={{ padding: '24px' }}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              <Text strong>Название правила</Text>
              <Input
                status={!hasValidName ? 'error' : undefined}
                placeholder="Введите название правила"
                value={currentRule.name}
                onChange={(e) => updateCurrentRule(prev => ({ ...prev, name: e.target.value }))}
              />
              {!hasValidName && (
                <Text type="danger" style={{ fontSize: 12 }}>
                  Название правила обязательно для заполнения
                </Text>
              )}
            </Space>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              <Text strong>Источник рекламы</Text>
              <AdSourceSelect
                value={formData.adSource || 'yandex_direct'}
                onChange={(v) => {
                  updateFormData(prev => ({
                    ...prev,
                    adSource: v,
                    accounts: [],
                    campaignIds: [],
                    adGroupIds: [],
                    adIds: []
                  }));
                  setShowFilters(false);
                  // Сбрасываем действия в правиле
                  updateCurrentRule(prev => ({
                    ...prev,
                    actions: {
                      ...prev.actions,
                      budgetAction: 'none',
                      actionType: 'none',
                      budgetAmount: 1
                    }
                  }));
                }}
              />
            </Space>
          </Col>
          <Col xs={24} lg={12}>
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              <Text strong>В каком рекламном кабинете?</Text>
              <AccountsMultiSelect
                value={formData.accounts || []}
                onChange={(ids) => updateFormData(prev => ({
                  ...prev,
                  accounts: ids,
                  // Каскадный сброс: при изменении аккаунта сбрасываем все фильтры
                  campaignIds: [],
                  adGroupIds: [],
                  adIds: []
                }))}
                options={getAccountsData()}
                mode="single"
                loading={dictionariesLoading}
                error={!formData.accounts || formData.accounts.length === 0}
              />
            </Space>
          </Col>
        </Row>

        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          <Text strong>Что проверяем?</Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <ScopeToggle value={scopeOne} onChange={setScopeOne} lockSelection={isEditMode} />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                color: 'rgba(0, 0, 0, 0.88)',
                fontSize: '14px'
              }}
              onClick={() => {
                handleFilterClick();
              }}
            >
              <FilterOutlined />
              <span>
                {getFilterButtonText()}
              </span>
            </div>
          </div>
        </Space>

        {showFilters && scopeOne === 'campaigns' && (
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Text strong>Кампании (название или ID)</Text>
            {formData.accounts && formData.accounts.length === 0 && (
              <Text type="secondary" style={{ fontSize: 12, color: '#ff4d4f' }}>
                Сначала выберите рекламный кабинет
              </Text>
            )}
            <CampaignsMultiSelect
              value={formData.campaignIds || []}
              onChange={(ids) => updateFormData(prev => ({
                ...prev,
                campaignIds: ids,
                // Каскадный сброс: при изменении кампаний сбрасываем группы и объявления
                adGroupIds: [],
                adIds: []
              }))}
              disabled={!formData.accounts || formData.accounts.length === 0}
              loading={dictionariesLoading}
              sourceId={formData.adSource === 'yandex_direct' ? 2 : 9}
              accountId={formData.accounts?.[0]}
            />
          </Space>
        )}
        {showFilters && scopeOne === 'adGroups' && (
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  <Text strong>По кампании</Text>
                  {formData.accounts && formData.accounts.length === 0 && (
                    <Text type="secondary" style={{ fontSize: 12, color: '#ff4d4f' }}>
                      Сначала выберите рекламный кабинет
                    </Text>
                  )}
                  <CampaignsMultiSelect
                    value={formData.campaignIds || []}
                    onChange={(ids) => updateFormData(prev => ({
                      ...prev,
                      campaignIds: ids,
                      // Каскадный сброс: при изменении кампаний сбрасываем группы и объявления
                      adGroupIds: [],
                      adIds: []
                    }))}
                    disabled={!formData.accounts || formData.accounts.length === 0}
                    loading={dictionariesLoading}
                    sourceId={formData.adSource === 'yandex_direct' ? 2 : 9}
                    accountId={formData.accounts?.[0]}
                  />
                </Space>
              </Col>
              <Col xs={24} lg={12}>
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  <Text strong>По группе объявлений</Text>
                  {formData.accounts && formData.accounts.length === 0 && (
                    <Text type="secondary" style={{ fontSize: 12, color: '#ff4d4f' }}>
                      Сначала выберите рекламный кабинет
                    </Text>
                  )}
                  <AdGroupsMultiSelect
                    value={formData.adGroupIds || []}
                    onChange={(ids) => updateFormData(prev => ({
                      ...prev,
                      adGroupIds: ids,
                      // Каскадный сброс: при изменении групп объявлений сбрасываем объявления
                      adIds: []
                    }))}
                    disabled={!formData.accounts || formData.accounts.length === 0}
                    loading={dictionariesLoading}
                    sourceId={formData.adSource === 'yandex_direct' ? 2 : 9}
                    accountId={formData.accounts?.[0]}
                    campaignIds={formData.campaignIds}
                  />
                </Space>
              </Col>
            </Row>
          </Space>
        )}
        {showFilters && scopeOne === 'ads' && (
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={8}>
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  <Text strong>По кампании</Text>
                  {formData.accounts && formData.accounts.length === 0 && (
                    <Text type="secondary" style={{ fontSize: 12, color: '#ff4d4f' }}>
                      Сначала выберите рекламный кабинет
                    </Text>
                  )}
                  <CampaignsMultiSelect
                    value={formData.campaignIds || []}
                    onChange={(ids) => updateFormData(prev => ({
                      ...prev,
                      campaignIds: ids,
                      // Каскадный сброс: при изменении кампаний сбрасываем группы и объявления
                      adGroupIds: [],
                      adIds: []
                    }))}
                    disabled={!formData.accounts || formData.accounts.length === 0}
                    loading={dictionariesLoading}
                    sourceId={formData.adSource === 'yandex_direct' ? 2 : 9}
                    accountId={formData.accounts?.[0]}
                  />
                </Space>
              </Col>
              <Col xs={24} lg={8}>
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  <Text strong>По группе объявлений</Text>
                  {formData.accounts && formData.accounts.length === 0 && (
                    <Text type="secondary" style={{ fontSize: 12, color: '#ff4d4f' }}>
                      Сначала выберите рекламный кабинет
                    </Text>
                  )}
                  <AdGroupsMultiSelect
                    value={formData.adGroupIds || []}
                    onChange={(ids) => updateFormData(prev => ({
                      ...prev,
                      adGroupIds: ids,
                      // Каскадный сброс: при изменении групп объявлений сбрасываем объявления
                      adIds: []
                    }))}
                    disabled={!formData.accounts || formData.accounts.length === 0}
                    loading={dictionariesLoading}
                    sourceId={formData.adSource === 'yandex_direct' ? 2 : 9}
                    accountId={formData.accounts?.[0]}
                    campaignIds={formData.campaignIds}
                  />
                </Space>
              </Col>
              <Col xs={24} lg={8}>
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  <Text strong>По объявлениям</Text>
                  {formData.accounts && formData.accounts.length === 0 && (
                    <Text type="secondary" style={{ fontSize: 12, color: '#ff4d4f' }}>
                      Сначала выберите рекламный кабинет
                    </Text>
                  )}
                  <AdsMultiSelect
                    value={formData.adIds || []}
                    onChange={(ids) => updateFormData(prev => ({ ...prev, adIds: ids }))}
                    disabled={!formData.accounts || formData.accounts.length === 0}
                    loading={dictionariesLoading}
                    sourceId={formData.adSource === 'yandex_direct' ? 2 : 9}
                    accountId={formData.accounts?.[0]}
                    campaignIds={formData.campaignIds}
                    groupIds={formData.adGroupIds}
                  />
                </Space>
              </Col>
            </Row>
          </Space>
        )}

        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Text strong>Условия</Text>
          <RuleBlockCard
            block={currentRule}
            onChange={handleRuleChange}
            scopeOne={scopeOne}
            adSource={formData.adSource}
            actionTypes={getActionTypesData() || undefined}
            telegramUsers={getTelegramUsers()}
            formData={formData}
          />
        </Space>

        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Text strong>Частота срабатывания правил</Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text>Каждые</Text>
            <Input
              type="number"
              min={1}
              value={formData.checkEveryDays || 5}
              onChange={(e) => updateFormData(prev => ({ ...prev, checkEveryDays: Number(e.target.value) || 1 }))}
              style={{ width: 80 }}
            />
            <Text>дней</Text>
          </div>
        </Space>
      </Space>
    </Modal>
  );
}
